import { Link } from 'react-router-dom';
import { useAuth } from '@clerk/react';
import { motion } from 'framer-motion';
import { ArrowRight, Sparkles, ShieldCheck, Zap, Code2, Plug } from 'lucide-react';
import { EmailAgentIllustration } from '@/components/illustrations/EmailAgent';
import { HowItWorksSection } from '@/components/HowItWorksSection';
import { FeatureDraftIllustration } from '@/components/illustrations/FeatureDraft';
import { FeatureToolsIllustration } from '@/components/illustrations/FeatureTools';
import { FeatureHumanIllustration } from '@/components/illustrations/FeatureHuman';
import { Logo } from '@/components/Logo';

/**
 * Marketing landing at `/`.
 *
 * Strict adherence to web/DESIGN.md:
 *  - Warm cream canvas (#faf9f6), off-black text (#111111), oat borders (#dedbd6)
 *  - Fin Orange (#ff5600) reserved for AI/brand accents
 *  - Saans stack (falls back to Inter); headings use tight line-height + negative tracking
 *  - 4px button radius, scale(1.08) hover (applied via .btn classes in index.css)
 *
 * Illustrations are hand-authored SVG in /components/illustrations — swap for
 * unDraw / Storyset / OpenDoodles downloads by replacing the component bodies.
 */

export function Landing() {
  const { isSignedIn } = useAuth();
  const primaryCta = isSignedIn ? '/app/inbox' : '/sign-up';
  const primaryCtaLabel = isSignedIn ? 'Open Inbox' : 'Get Started';

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Nav isSignedIn={Boolean(isSignedIn)} />

      {/* ------------------------------ Hero ------------------------------ */}
      <section className="max-w-6xl mx-auto px-6 pt-16 pb-24 md:pt-24 md:pb-28 grid md:grid-cols-[1.1fr_1fr] gap-12 items-center">
        <div>
          <div className="mono-label mb-4">AI customer support · native to email</div>
          <h1 className="headline text-5xl md:text-7xl font-normal max-w-[15ch]">
            Stop Answering the Same Ticket Twice.
          </h1>
          <p className="mt-6 text-lg max-w-prose leading-body text-foreground/80">
            <span translate="no">edgemail</span> runs an AI agent on every support thread. It reads the message,
            checks your tools — <span translate="no">Stripe</span>, <span translate="no">Linear</span>, your own APIs, any MCP server — and
            drafts a reply in your voice. You approve with one click, or it sends
            itself when it’s confident.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to={primaryCta} className="btn-accent gap-2 px-5 h-11 text-base">
              {primaryCtaLabel}
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </Link>
            <a href="#how" className="btn-secondary px-5 h-11 text-base">
              See How It Works
            </a>
          </div>
          <TrustRow />
        </div>
        <div className="relative">
          <EmailAgentIllustration className="w-full h-auto max-w-[520px] mx-auto" />
        </div>
      </section>

      {/* ------------------------------ Features --------------------------- */}
      <section className="bg-surface border-y border-border">
        <div className="max-w-6xl mx-auto px-6 py-20">
          <div className="max-w-2xl mb-12">
            <div className="mono-label mb-3">What it does</div>
            <h2 className="text-4xl md:text-5xl font-normal tracking-sub leading-heading">
              The Loop You’d Hire a Support Engineer to Build.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <Feature
              illustration={FeatureDraftIllustration}
              title="AI That Actually Drafts"
              body="Reasoner + drafter models on Workers AI. Tool-calling, JSON-schema guardrails, confidence scoring per reply."
            />
            <Feature
              illustration={FeatureToolsIllustration}
              title="Every Tool Your Team Uses"
              body="Composio (300+ SaaS), custom HTTP endpoints, MCP servers, built-in ticket ops. Per-org, one click to connect."
            />
            <Feature
              illustration={FeatureHumanIllustration}
              title="Human in the Loop When It Counts"
              body="Low-confidence or risky actions wait for approval. Watch agent activity live via WebSockets."
            />
          </div>
        </div>
      </section>

      {/* ------------------------------ How it works ----------------------- */}
      <HowItWorksSection />

      {/* ------------------------------ Tools ------------------------------ */}
      <section className="bg-surface border-y border-border">
        <div className="max-w-6xl mx-auto px-6 py-20 grid md:grid-cols-[1fr_1.1fr] gap-12 items-center">
          <div>
            <div className="mono-label mb-3">The tool layer</div>
            <h2 className="text-4xl md:text-5xl font-normal tracking-sub leading-heading">
              One Agent. Every System.
            </h2>
            <p className="mt-5 text-base leading-body text-foreground/80 max-w-prose">
              The agent speaks to <span translate="no">Stripe</span>, <span translate="no">Linear</span>, <span translate="no">Gmail</span>, <span translate="no">HubSpot</span>, <span translate="no">Slack</span>, <span translate="no">Intercom</span> and
              300+ more via <b><span translate="no">Composio</span></b>. Point it at your own APIs with a JSON
              schema. Connect any <b>MCP server</b> your stack already exposes.
              Auth secrets are AES-GCM-encrypted at rest.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3 max-w-md">
              <MiniFeature icon={Zap} label="Composio 300+" />
              <MiniFeature icon={Code2} label="Custom HTTP" />
              <MiniFeature icon={Plug} label="MCP Servers" />
              <MiniFeature icon={ShieldCheck} label="Per-Call Approval" />
            </div>
          </div>
          <div>
            <IntegrationsGrid />
          </div>
        </div>
      </section>

      {/* ------------------------------ Final CTA -------------------------- */}
      <section className="max-w-4xl mx-auto px-6 py-24 text-center">
        <h2 className="headline text-4xl md:text-6xl font-normal">
          Turn Your Inbox Into Your Best Support Engineer.
        </h2>
        <p className="mt-5 text-base text-foreground/70 max-w-xl mx-auto leading-body">
          Free to try. Pay only for the AI calls and email you actually send.
        </p>
        <div className="mt-8 flex flex-wrap gap-3 justify-center">
          <Link to={primaryCta} className="btn-accent gap-2 px-6 h-12 text-base">
            {primaryCtaLabel}
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
          <a href="https://github.com" className="btn-secondary px-6 h-12 text-base" rel="noopener">
            View on GitHub
          </a>
        </div>
      </section>

      {/* ------------------------------ Footer ----------------------------- */}
      <footer className="border-t border-border">
        <div className="max-w-6xl mx-auto px-6 py-8 flex flex-wrap items-center justify-between gap-4 text-sm">
          <div className="flex items-center gap-3">
            <Logo variant="full" size={48} />
            <span className="text-muted-foreground">AI customer support on the edge</span>
          </div>
          <div className="flex items-center gap-4 mono-label">
            <span>Built on Cloudflare</span>
            <span aria-hidden>·</span>
            <span>Auth by Clerk</span>
            <span aria-hidden>·</span>
            <span>Tools via Composio + MCP</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bits
// ---------------------------------------------------------------------------

function Nav({ isSignedIn }: { isSignedIn: boolean }) {
  return (
    <nav className="sticky top-0 z-10 bg-background/85 backdrop-blur border-b border-border">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" aria-label="edgemail — home" className="inline-flex items-center">
          <Logo variant="full" size={56} />
        </Link>
        <div className="flex items-center gap-2">
          <a href="#how" className="hidden sm:inline-block px-3 py-1.5 text-sm text-foreground/80 hover:text-foreground">
            How It Works
          </a>
          {isSignedIn ? (
            <Link to="/app/inbox" className="btn-primary h-9 px-4">Open Inbox</Link>
          ) : (
            <>
              <Link to="/sign-in" className="btn-ghost h-9 px-3 text-sm">Sign In</Link>
              <Link to="/sign-up" className="btn-accent h-9 px-4 text-sm">Get Started</Link>
            </>
          )}
        </div>
      </div>
    </nav>
  );
}

function TrustRow() {
  return (
    <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 mono-label">
      <span>Runs on Cloudflare Workers</span>
      <span className="hidden md:inline" aria-hidden>·</span>
      <span>Durable Objects per thread</span>
      <span className="hidden md:inline" aria-hidden>·</span>
      <span>Encrypted secrets</span>
      <span className="hidden md:inline" aria-hidden>·</span>
      <span>Open WebSocket protocol</span>
    </div>
  );
}

function Feature({
  illustration: Illustration,
  title,
  body,
}: {
  illustration: React.ElementType;
  title: string;
  body: string;
}) {
  return (
    <div className="card p-0 overflow-hidden flex flex-col">
      <div className="bg-muted border-b border-border aspect-video grid place-items-center relative">
        <Illustration className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="p-6">
        <h3 className="text-xl font-normal tracking-sub leading-heading mb-2">{title}</h3>
        <p className="text-sm leading-body text-foreground/75">{body}</p>
      </div>
    </div>
  );
}

function MiniFeature({ icon: Icon, label }: { icon: typeof Sparkles; label: string }) {
  return (
    <div className="flex items-center gap-2 rounded-sm border border-border bg-background px-3 py-2 text-sm">
      <Icon className="h-4 w-4 text-foreground/70" aria-hidden="true" />
      {label}
    </div>
  );
}

function IntegrationsGrid() {
  const tools = [
    { name: 'Stripe', domain: 'stripe.com' },
    { name: 'Linear', domain: 'linear.app' },
    { name: 'Gmail', domain: 'mail.google.com' },
    { name: 'HubSpot', domain: 'hubspot.com' },
    { name: 'Slack', domain: 'slack.com' },
    { name: 'Intercom', domain: 'intercom.com' },
    { name: 'Jira', domain: 'jira.com' },
    { name: 'Salesforce', domain: 'salesforce.com' },
    { name: 'GitHub', domain: 'github.com' },
  ];

  return (
    <div className="relative w-full max-w-[420px] mx-auto aspect-square">
      <div className="grid grid-cols-3 gap-4 lg:gap-5 place-content-center h-full">
        {tools.map((tool, i) => (
          <motion.div
            key={tool.name}
            className="w-20 h-20 sm:w-24 sm:h-24 mx-auto bg-white border border-border shadow-sm rounded-xl flex items-center justify-center group cursor-default"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-50px" }}
            transition={{ delay: i * 0.06, type: 'spring', stiffness: 350, damping: 25 }}
            whileHover={{ scale: 1.08, y: -4 }}
          >
            <img 
              src={`https://t3.gstatic.com/faviconV2?client=SOCIAL&type=FAVICON&fallback_opts=TYPE,SIZE,URL&url=https://${tool.domain}&size=128`} 
              alt={`${tool.name} logo`} 
              className="w-10 h-10 sm:w-12 sm:h-12 group-hover:scale-110 transition-transform duration-300 rounded-sm" 
              loading="lazy" 
              width="48"
              height="48"
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
}
